import React from 'react'
import { CSSObject } from '@emotion/react'

import { Box, DisplayPosition, Image, withCitrusTheme } from '@gousto-internal/citrus-react'

import heroBackgroundDesktop from '../../../../assets/HeroAssets/hero-desktop.jpg'
import heroBackgroundDesktopWebp from '../../../../assets/HeroAssets/hero-desktop.webp'
import heroBackgroundMobile from '../../../../assets/HeroAssets/hero-mobile.jpg'
import heroBackgroundMobileWebp from '../../../../assets/HeroAssets/hero-mobile.webp'
import heroBackgroundTablet from '../../../../assets/HeroAssets/hero-kitchen.jpg'
import heroBackgroundTabletWebp from '../../../../assets/HeroAssets/hero-kitchen.webp'

const BACKGROUND_ALT =
  'A man in a home kitchen unpacking a Gousto box of fresh ingredients on the counter, next to a ' +
  'bowl of cooked noodles and a recipe card'

// Per-band framing values, their derivations and measured contrast: see ui-design.md and design.md
// ("Implementation notes") in openspec/changes/update-cwc-hero-image-and-whats-new-cta/.
const HeroBackgroundImage = withCitrusTheme(Image, (theme) => [
  {
    position: 'absolute',
    maxWidth: 'none',
    objectFit: 'cover',
    width: '100%',
    height: '100%',
    left: '0',
    top: '0',
  } as CSSObject,
  // Keep as responsiveStyle, and keep the two overrides below it: all three are min-width:1024px-scoped,
  // so a flat rule or a reorder silently wins over them.
  //
  // Mobile (<640px) is centred horizontally — its asset is pre-cropped to the design's own mobile
  // framing, so any X offset departs from the crop the designer approved.
  //
  // Y is `0%`, not `50%`, so that where Hero is wider in aspect than the asset (0.641) the discarded
  // height comes off the BOTTOM only — the counter and recipe cards, most of which sit behind the
  // discount card anyway — instead of being split top and bottom. Splitting it lifts the whole scene by
  // half the overflow and hides the box's ingredients behind the headline's last line: on a 390×570 Hero
  // the ingredient band lands at y156–214 against a headline bottom of y218, i.e. fully covered. `0%`
  // drops it to y175–234 and reveals ~16px, which is what design signed off. Y is inert on taller phones
  // (no vertical overflow to move). Costs a little contrast — see ui-design.md for the measurements.
  theme.responsiveStyle(
    ['50% 0%', '47% 52%', '47% 52%', '50% 50%'],
    ['objectPosition'],
  ) as CSSObject,
  {
    '@media (min-width: 1024px) and (max-width: 1279px)': {
      objectPosition: '67% 50%',
    },
  } as CSSObject,
  {
    '@media (min-width: 1280px) and (min-aspect-ratio: 5/3)': {
      objectPosition: '50% 65%',
    },
  } as CSSObject,
])

// Legibility scrims. Each is the lightest value that holds WCAG AA over this photograph — do not lighten
// them further without re-measuring contrast. Mobile and desktop now carry Figma's own alphas; mobile's
// fade-out stop and tablet's bottom alpha still depart from it because Figma's values measure below AA.
const MOBILE_GRADIENT =
  'linear-gradient(180deg, rgba(0,0,0,0.65) 17.695%, rgba(7,9,11,0) 68%, rgba(7,9,11,0) 70.236%, rgba(0,0,0,0.65) 87.75%)'
const TABLET_GRADIENT =
  'linear-gradient(180deg, rgba(0,0,0,0.5) 25%, rgba(7,9,11,0) 44.557%, rgba(7,9,11,0) 77.888%, rgba(0,0,0,0.3) 89.509%)'
const DESKTOP_GRADIENT =
  'linear-gradient(99.207deg, rgba(0,0,0,0.5) 29.295%, rgba(0,0,0,0) 65.755%)'
// The 1024–1279 band has no Figma frame and cannot be lightened: 0.7 already measures 2.93:1 on the
// headline at 1024×1366 against a 3:1 requirement.
const NARROW_DESKTOP_GRADIENT = 'linear-gradient(99.207deg, rgba(0,0,0,0.8) 25%, rgba(0,0,0,0) 88%)'
const GradientOverlay = withCitrusTheme(Box, (theme) => [
  theme.responsiveStyle(
    [MOBILE_GRADIENT, MOBILE_GRADIENT, TABLET_GRADIENT, DESKTOP_GRADIENT],
    ['background'],
  ) as CSSObject,
  {
    '@media (min-width: 1024px) and (max-width: 1279px)': {
      background: NARROW_DESKTOP_GRADIENT,
    },
  } as CSSObject,
])

// Media-gated <source>s so only the matched crop downloads, ordered narrowest-gate-last. The <img> `src`
// must stay the smallest asset (now the mobile crop): a fallback `src` downloads regardless, so gating
// only the <source>s is not enough.
export function CWCHeroBackground() {
  return (
    <>
      <picture style={{ display: 'contents' }}>
        <source media="(min-width: 1024px)" type="image/webp" srcSet={heroBackgroundDesktopWebp} />
        <source media="(min-width: 1024px)" srcSet={heroBackgroundDesktop} />
        <source media="(min-width: 640px)" type="image/webp" srcSet={heroBackgroundTabletWebp} />
        <source media="(min-width: 640px)" srcSet={heroBackgroundTablet} />
        <source type="image/webp" srcSet={heroBackgroundMobileWebp} />
        <HeroBackgroundImage src={heroBackgroundMobile} alt={BACKGROUND_ALT} />
      </picture>
      <GradientOverlay
        position={DisplayPosition.Absolute}
        top="0"
        left="0"
        width="100%"
        height="100%"
      />
    </>
  )
}
